import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Modal, Button, Loader } from 'shared-components';
import { get, post } from 'shared-components/src/utils/http';
import {
  ModalContent,
  CloseButton,
  StepIndicator,
  StepDot,
  IconContainer,
  Heading,
  Paragraph,
  ButtonWrapper,
  DetailsTable,
  DetailRow,
  DetailLabel,
  DetailValue,
  PrivacyBox,
  PrivacyHeading,
  PrivacyText,
  ConsentText,
  ButtonRow,
  CheckboxRow,
  SuccessContainer,
  SuccessIcon,
  SuccessTitle,
  SuccessDescription,
} from './styles';

const TOTAL_STEPS = 3;

const BankIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" x2="21" y1="22" y2="22" />
    <line x1="6" x2="6" y1="18" y2="11" />
    <line x1="10" x2="10" y1="18" y2="11" />
    <line x1="14" x2="14" y1="18" y2="11" />
    <line x1="18" x2="18" y1="18" y2="11" />
    <polygon points="12 2 20 7 4 7" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

// Convert "14-03-1989" (DD-MM-YYYY) to "03/14/1989" (MM/DD/YYYY)
const formatDob = (dob) => {
  if (!dob) return '';
  const parts = dob.split('-');
  if (parts.length !== 3) return dob;
  const [day, month, year] = parts;
  return `${month}/${day}/${year}`;
};

// Format masked SIN "******789" to "*** *** 789"
const formatMaskedSin = (masked) => {
  if (!masked) return '';
  const digits = masked.replace(/[^0-9*]/g, '');
  if (digits.length === 9) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return masked;
};

const StepOne = ({ intl, onNext }) => (
  <>
    <IconContainer>
      <BankIcon />
    </IconContainer>
    <Heading>{intl.messages['cra-dda-step1-heading']}</Heading>
    <Paragraph>{intl.messages['cra-dda-step1-description']}</Paragraph>
    <Paragraph>{intl.messages['cra-dda-step1-notice']}</Paragraph>
    <Paragraph>
      <strong>{intl.messages['cra-dda-step1-notice-bold']}</strong>
    </Paragraph>
    <ButtonWrapper>
      <Button type="primary" size="large" onClick={onNext}>
        {intl.messages['cra-dda-step1-button']}
      </Button>
    </ButtonWrapper>
  </>
);

StepOne.propTypes = {
  intl: PropTypes.shape({ messages: PropTypes.object }).isRequired,
  onNext: PropTypes.func.isRequired,
};

const StepTwo = ({ intl, cardholder, account, accountId, onBack, onNext }) => {
  const [maskedSin, setMaskedSin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSin = async () => {
      try {
        const res = await get(
          `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/masked-sin`
        );
        setMaskedSin(res.masked_sin);
      } catch (e) {
        setMaskedSin('---');
      }
      setLoading(false);
    };
    fetchSin();
  }, [accountId]);

  if (loading) return <Loader />;

  const fullName = `${cardholder.first_name} ${cardholder.last_name}`;
  const dob = formatDob(cardholder.date_of_birth);
  const sin = formatMaskedSin(maskedSin);
  const bankDetails = account.bank_details || {};

  return (
    <>
      <Paragraph>{intl.messages['cra-dda-step2-description']}</Paragraph>
      <DetailsTable>
        <DetailRow>
          <DetailLabel>{intl.messages['cra-dda-step2-full-name']}:</DetailLabel>
          <DetailValue>{fullName}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{intl.messages['cra-dda-step2-dob']}:</DetailLabel>
          <DetailValue>{dob}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{intl.messages['cra-dda-step2-sin']}:</DetailLabel>
          <DetailValue>{sin}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{intl.messages['cra-dda-step2-transit']}:</DetailLabel>
          <DetailValue>{bankDetails.transit_number}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{intl.messages['cra-dda-step2-institution']}:</DetailLabel>
          <DetailValue>{bankDetails.institution_number}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{intl.messages['cra-dda-step2-account']}:</DetailLabel>
          <DetailValue>{bankDetails.account_number}</DetailValue>
        </DetailRow>
      </DetailsTable>
      <PrivacyHeading>{intl.messages['cra-dda-step2-privacy-heading']}</PrivacyHeading>
      <PrivacyBox>
        <PrivacyText>{intl.messages['cra-dda-step2-privacy-text']}</PrivacyText>
      </PrivacyBox>
      <ConsentText>{intl.messages['cra-dda-step2-privacy-consent']}</ConsentText>
      <ButtonRow>
        <Button type="secondary" size="large" onClick={onBack}>
          {intl.messages['cra-dda-step2-back']}
        </Button>
        <Button type="primary" size="large" onClick={onNext}>
          {intl.messages['cra-dda-step2-continue']}
        </Button>
      </ButtonRow>
    </>
  );
};

StepTwo.propTypes = {
  intl: PropTypes.shape({ messages: PropTypes.object }).isRequired,
  cardholder: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    date_of_birth: PropTypes.string,
  }).isRequired,
  account: PropTypes.shape({
    bank_details: PropTypes.shape({
      transit_number: PropTypes.string,
      institution_number: PropTypes.string,
      account_number: PropTypes.string,
    }),
  }).isRequired,
  accountId: PropTypes.number.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

const StepThree = ({ intl, accountId, onBack, onSubmitSuccess }) => {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!agreed) return;
    setSubmitting(true);
    setError(null);
    try {
      await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/cra-dda-participant`,
        { channel: '01' }
      );
      onSubmitSuccess();
    } catch (e) {
      setError(true);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Paragraph>{intl.messages['cra-dda-step3-paragraph1']}</Paragraph>
      <Paragraph>{intl.messages['cra-dda-step3-paragraph2']}</Paragraph>
      <CheckboxRow>
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <span>{intl.messages['cra-dda-step3-checkbox']}</span>
      </CheckboxRow>
      {error && (
        <Paragraph style={{ color: '#d32f2f', marginTop: '12px' }}>
          Something went wrong. Please try again.
        </Paragraph>
      )}
      <ButtonRow>
        <Button type="secondary" size="large" onClick={onBack} disabled={submitting}>
          {intl.messages['cra-dda-step3-back']}
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleSubmit}
          disabled={!agreed || submitting}
        >
          {submitting ? <Loader /> : intl.messages['cra-dda-step3-submit']}
        </Button>
      </ButtonRow>
    </>
  );
};

StepThree.propTypes = {
  intl: PropTypes.shape({ messages: PropTypes.object }).isRequired,
  accountId: PropTypes.number.isRequired,
  onBack: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
};

const SuccessScreen = ({ intl, onDone }) => (
  <SuccessContainer>
    <SuccessIcon>
      <CheckIcon />
    </SuccessIcon>
    <SuccessTitle>{intl.messages['cra-dda-success-title']}</SuccessTitle>
    <SuccessDescription>{intl.messages['cra-dda-success-description']}</SuccessDescription>
    <SuccessDescription>{intl.messages['cra-dda-success-description2']}</SuccessDescription>
    <ButtonWrapper style={{ width: '100%' }}>
      <Button type="primary" size="large" onClick={onDone}>
        {intl.messages['cra-dda-success-done']}
      </Button>
    </ButtonWrapper>
  </SuccessContainer>
);

SuccessScreen.propTypes = {
  intl: PropTypes.shape({ messages: PropTypes.object }).isRequired,
  onDone: PropTypes.func.isRequired,
};

const CraDdaEnrollModal = ({ onClose, accountId, cardholder, account }) => {
  const intl = useIntl();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmitSuccess = () => {
    setSubmitted(true);
  };

  const getStepTitle = () => {
    if (submitted) return '';
    if (currentStep === 2) return intl.messages['cra-dda-step2-title'];
    if (currentStep === 3) return intl.messages['cra-dda-step3-title'];
    return intl.messages['cra-dda-modal-title'];
  };

  const renderStepDot = (stepNum) => {
    if (stepNum < currentStep || submitted) {
      return <StepDot key={stepNum} active completed>&#10003;</StepDot>;
    }
    return (
      <StepDot key={stepNum} active={currentStep === stepNum}>
        {stepNum}
      </StepDot>
    );
  };

  const renderContent = () => {
    if (submitted) {
      return <SuccessScreen intl={intl} onDone={onClose} />;
    }

    switch (currentStep) {
    case 1:
      return <StepOne intl={intl} onNext={handleNext} />;
    case 2:
      return (
        <StepTwo
          intl={intl}
          cardholder={cardholder}
          account={account}
          accountId={accountId}
          onBack={handleBack}
          onNext={handleNext}
        />
      );
    case 3:
      return (
        <StepThree
          intl={intl}
          accountId={accountId}
          onBack={handleBack}
          onSubmitSuccess={handleSubmitSuccess}
        />
      );
    default:
      return null;
    }
  };

  const title = getStepTitle();

  return (
    <Modal>
      <ModalContent>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        {title && (
          <Heading as="h2" style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
            {title}
          </Heading>
        )}
        {!submitted && (
          <StepIndicator>
            {Array.from({ length: TOTAL_STEPS }, (_, i) => renderStepDot(i + 1))}
          </StepIndicator>
        )}
        {renderContent()}
      </ModalContent>
    </Modal>
  );
};

CraDdaEnrollModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  accountId: PropTypes.number.isRequired,
  cardholder: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
};

export default CraDdaEnrollModal;
